import { Children, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dialog, storage } from 'app/utils';
import { useLoader, useMountEffect } from 'app/hooks';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Table, ITable, IPaging } from 'app/components/data';
import { specialties } from 'app/services';
import { ISpecialty } from 'app/models';

const stateKey = 'modules.specialties';
const loader = (params: IPaging) => {
  storage.set(stateKey, params);
  const { page, rows, sort } = params;

  return specialties.query({ sort: sort?.field, order: sort?.order, skip: (page * rows), take: rows });
};

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const types: Record<number, string> = useMemo(() => ({
    1: t('app.profile.employee'),
    2: t('app.profile.employer'),
  }), [t]);

  const statuses: Record<number, string> = useMemo(() => ({
    0: t('app.status.disabled'),
    1: t('app.status.enabled'),
    2: t('app.status.approved'),
  }), [t]);

  const { loading, data, total, params, reload } = useLoader(loader);

  const actions: Record<string, (item: ISpecialty) => Promise<any> | void> = useMemo(() => ({
    delete: (item) => dialog.confirm({
      message: t('specialties.delete.question', { ...item }),
      accept: { label: t('app.delete'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) specialties.delete(item).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`specialties.delete.success`, { ...item }) });

        return reload(params);
      });
    }),
  }), [params, reload, t]);

  const table: ITable<ISpecialty> = { data, total, loading,
    className: ['min-h-0', 'flex-auto'],
    columns: [
      { field: 'name', type: 'string', header: t('specialties.fields.name'), sortable: true },
      { field: 'type', type: 'number', header: { label: t('profiles.fields.type'), className: 'w-10rem' }, sortable: true,
        template: ({ type }) => types[type],
      },
      { field: 'professionId', type: 'number', header: { label: t('profiles.fields.profession'), className: 'w-15rem' }, filter: true,
        source: { type: 'lookup', key: 'professions' },
      },
      { field: 'status', type: 'boolean', header: { label: t('specialties.fields.status'), className: 'w-6rem' },
        source: { type: 'static',
          data: [
            { value: 0, label: statuses[0] },
            { value: 1, label: statuses[1] },
            { value: 2, label: statuses[2] },
          ],
        },
        // template: (item) => statuses[item.status],
      },
      { field: 'actions', align: 'right', header: { className: 'w-6rem' }, className: 'p-0',
        template: (item) => Children.toArray([
          <Button href={`${item.id}`} text rounded icon="mdi-edit" />,
          <Button severity="danger" text rounded icon="mdi-delete" onClick={() => actions['delete'](item)} />,
        ]),
      },
    ],
    dataKey: 'id',
    removableSort: true,
    resizableColumns: true,
    scrollable: true,
    scrollHeight: 'flex',
    selectionMode: 'single',
    paginator: { show: true, page: params?.page, rows: params?.rows, options: [20, 50, 100], className: 'border-none' },
    sort: params?.sort,

    onLoad: reload,
    onRowDoubleClick: (item) => navigate(item.id),
  };

  useMountEffect(() => {
    reload(storage.get(stateKey) ?? { page: 0, rows: 20 }).then();
  });

  return (
    <div className="h-full flex flex-column">
      <div className="flex flex-none surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text icon="mdi-cached" severity="success" className="mr-2 px-3 py-1" disabled={loading} onClick={() => reload(params)} />
          <Button href="new" icon="mdi-add" label={t('specialties.new')} className="px-3 py-1" />
        </div>
      </div>
      <Table { ...table } />
    </div>
  );
});
